/* The Map — coverage, not a graph (CR-021).
 *
 * This replaces the pan/zoom node graph. The graph answered "how do these ten
 * Topics relate" on a surface that had to be pinched to read; the ledger behind
 * it is 1,449 competency items across sixteen domains, and Expedition 1 alone is
 * twenty-five Topics. A graph gets less legible with every one of those; a list
 * of domains gets longer, which a phone handles natively.
 *
 * It is also the navigation surface — every Unit is reachable from here — so it
 * answers "what have I covered" and "where do I go" with the same object.
 *
 * Everything it needs already ships: /api/atlas carries clusters, domains,
 * topics with their units and mastery, and completion is tracked locally by
 * isUnitCompleted(). No new endpoint.
 */

import { useEffect, useMemo, useState } from 'react';
import { api, ApiFailure, type AtlasResponse, type AtlasTopicDto } from '../api';
import { DESIGN_TOKENS } from '@ascent/shared';
import { isUnitCompleted } from '../sync';

const CLUSTER_HUE: Record<string, string> = DESIGN_TOKENS.cluster;
const DEFAULT_HUE = DESIGN_TOKENS.cluster.C1;

const LEVEL_NAMES = ['Unseen', 'Studied', 'Practiced', 'Assessed', 'Applied'];

const UNIT_LABEL: Record<string, string> = {
  lesson: 'Lesson', drill_deck: 'Drills', quiz: 'Quiz',
  mission: 'Mission', lab: 'Lab', build: 'Build',
};

/** A build task is the work; everything else supports it. Ordering says so. */
const UNIT_ORDER: Record<string, number> = {
  build: 0, mission: 1, lab: 2, lesson: 3, drill_deck: 4, quiz: 5,
};

export function MapScreen({
  domainId, onOpenDomain, onOpenUnit,
}: {
  domainId: string | null;
  onOpenDomain: (id: string | null) => void;
  onOpenUnit: (unitId: string) => void;
}) {
  const [atlas, setAtlas] = useState<AtlasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  useEffect(() => {
    void (async () => {
      try {
        const a = await api.atlas();
        setAtlas(a);
        const completed = new Set<string>();
        for (const t of a.topics) {
          for (const u of t.units) {
            if (await isUnitCompleted(u.unit_id)) completed.add(u.unit_id);
          }
        }
        setDone(completed);
      } catch (err) {
        setError(
          err instanceof ApiFailure
            ? err.status === 404 ? 'no-release' : err.message
            : 'Something went wrong.',
        );
      }
    })();
  }, []);

  const masteryOf = useMemo(() => {
    const m = new Map<string, { level: number; eroding: boolean }>();
    for (const row of atlas?.mastery ?? []) {
      m.set(row.topic_id, { level: row.level, eroding: row.eroding });
    }
    return m;
  }, [atlas]);

  const topicsByDomain = useMemo(() => {
    const m = new Map<string, AtlasTopicDto[]>();
    for (const t of atlas?.topics ?? []) {
      m.set(t.domain, [...(m.get(t.domain) ?? []), t]);
    }
    return m;
  }, [atlas]);

  if (error === 'no-release') {
    return (
      <main className="screen">
        <p className="eyebrow">Map</p>
        <h1>No map yet</h1>
        <p>
          The Map appears once the Factory activates a Content Release. Before the
          first one there is genuinely nothing to orient against — this is the
          expected state, not an error.
        </p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="screen">
        <p className="eyebrow">Map</p>
        <h1>Could not load the Map</h1>
        <p>{error}</p>
      </main>
    );
  }
  if (!atlas) {
    return (
      <main className="screen">
        <p className="eyebrow">Map</p>
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--block" />
      </main>
    );
  }

  // ---- one domain, opened ------------------------------------------------
  if (domainId) {
    const domain = atlas.domains.find((d) => d.domain_id === domainId);
    const topics = topicsByDomain.get(domainId) ?? [];
    const hue = CLUSTER_HUE[domain?.cluster_id ?? ''] ?? DEFAULT_HUE;

    return (
      <main className="screen">
        <button type="button" className="back" onClick={() => onOpenDomain(null)}>← Map</button>
        <p className="eyebrow" style={{ color: hue }}>{domain?.domain_id}</p>
        <h1>{domain?.name ?? domainId}</h1>

        {topics.length === 0 ? (
          <p className="hint">
            No Topics have been authored in this domain yet. It is in the profile
            and on the roadmap; the content is not written.
          </p>
        ) : null}

        {topics.map((t) => {
          const m = masteryOf.get(t.topic_id);
          const units = [...t.units].sort(
            (a, b) => (UNIT_ORDER[a.unit_type] ?? 9) - (UNIT_ORDER[b.unit_type] ?? 9),
          );
          return (
            <section className="card" key={t.topic_id}>
              <div className="card__title">
                <span>{t.title}</span>
                <span className="card__meta">
                  {LEVEL_NAMES[m?.level ?? 0]}{m?.eroding ? ' · eroding' : ''}
                </span>
              </div>
              <ul className="unitlist">
                {units.map((u) => (
                  <li key={u.unit_id}>
                    <button
                      type="button"
                      className="unitlist__btn"
                      onClick={() => onOpenUnit(u.unit_id)}
                    >
                      <span className="unitlist__type">
                        {UNIT_LABEL[u.unit_type] ?? u.unit_type}
                      </span>
                      <span className="unitlist__title">{u.title}</span>
                      <span className="unitlist__note">
                        {done.has(u.unit_id) ? 'done' : `${u.est_minutes} min`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>
    );
  }

  // ---- all sixteen domains, by cluster -----------------------------------
  return (
    <main className="screen">
      <p className="eyebrow">Map</p>
      <h1>Where the ground is covered</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        Sixteen domains. A domain with no Topics yet is in the profile but not
        written — that gap is real, and showing it is the point.
      </p>

      {atlas.clusters.map((cluster) => {
        const hue = CLUSTER_HUE[cluster.cluster_id] ?? DEFAULT_HUE;
        const domains = atlas.domains.filter((d) => d.cluster_id === cluster.cluster_id);
        return (
          <section className="mapcluster" key={cluster.cluster_id}>
            <p className="mapcluster__name" style={{ color: hue }}>{cluster.name}</p>
            {domains.map((d) => {
              const topics = topicsByDomain.get(d.domain_id) ?? [];
              const started = topics.filter((t) => (masteryOf.get(t.topic_id)?.level ?? 0) > 0);
              return (
                <button
                  type="button"
                  className="mapdomain"
                  key={d.domain_id}
                  onClick={() => onOpenDomain(d.domain_id)}
                  disabled={topics.length === 0}
                >
                  <span className="mapdomain__id" style={{ color: hue }}>{d.domain_id}</span>
                  <span className="mapdomain__name">{d.name}</span>
                  <span className="mapdomain__count">
                    {topics.length === 0
                      ? 'not written'
                      : `${started.length}/${topics.length}`}
                  </span>
                  {/* A bar per Topic, filled by mastery. Legible at a glance and
                      it degrades gracefully as the curriculum grows. */}
                  <span className="mapdomain__bars">
                    {topics.slice(0, 12).map((t) => {
                      const lvl = masteryOf.get(t.topic_id)?.level ?? 0;
                      return (
                        <span
                          key={t.topic_id}
                          className="mapdomain__bar"
                          style={{
                            background: hue,
                            opacity: lvl === 0 ? 0.16 : 0.3 + lvl * 0.175,
                          }}
                        />
                      );
                    })}
                  </span>
                </button>
              );
            })}
          </section>
        );
      })}
    </main>
  );
}
